/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { humanizeLearningTime } from 'js/utils/DateTimeUtils';

import { Divider, Grid, Typography2, breakpoints } from '@coursera/cds-core';
import type { IconProps } from '@coursera/cds-core';
import type { EventData } from '@coursera/event-pulse/react';

import SkillAssessmentCourseActionButton from 'bundles/skills-common/shared/verified-skills/SkillAssessmentCourseActionButton';

import _t from 'i18n!nls/skills-common';

export type Props = {
  courseName: string;
  timeCommitment?: number;
  // Props for SkillAssessmentCourseActionButton
  isEnrolledInPathway: boolean;
  isEnrolledInCourse: boolean;
  isProgramMember: boolean;
  courseId: string;
  courseSlug: string;
  programId: string;
  redirectToAssessment?: boolean;
  assessmentId?: string;
  pathwayCollectionId?: string;
  pathwayId?: string;
  programSlug?: string;
  thirdPartyOrganizationSlug?: string;
  disabled?: boolean;
  buttonText?: string;
  buttonIcon?: React.ReactElement<IconProps>;
  buttonIconPosition?: 'before' | 'after';
  activateProgramAccessModal?: () => void;
  isEnrollingCallback?: (currentEnrollingAction: string | undefined) => void;
  trackingData?: EventData<'click_button'>;
};

const styles = {
  container: css`
    padding: var(--cds-spacing-200) 0 0 var(--cds-spacing-200);
  `,
  gridContainer: css`
    padding-left: var(--cds-spacing-50);
  `,
  title: css`
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
  `,
  supportText: css`
    display: flex;
    flex-direction: row;
    gap: 0 var(--cds-spacing-100);
    align-items: center;
    flex-wrap: wrap;
  `,
  buttonContainer: css`
    display: flex;
    justify-content: flex-end;

    ${breakpoints.down('sm')} {
      justify-content: flex-start;
      margin-top: var(--cds-spacing-200);
    }
  `,
  divider: css`
    margin-top: var(--cds-spacing-150);
  `,
};

const SkillProgressTakeAssessmentControls = ({
  courseName,
  timeCommitment,
  isEnrolledInPathway,
  isEnrolledInCourse,
  isProgramMember,
  courseId,
  courseSlug,
  programId,
  redirectToAssessment,
  assessmentId,
  pathwayCollectionId,
  pathwayId,
  programSlug,
  thirdPartyOrganizationSlug,
  disabled,
  buttonText,
  buttonIcon,
  buttonIconPosition,
  activateProgramAccessModal,
  isEnrollingCallback,
  trackingData,
}: Props) => {
  // get human readable time commitment if it exists
  const formattedTimeCommitment = timeCommitment ? humanizeLearningTime(timeCommitment) : null;

  return (
    <div data-testid="skills-assessment-enrol-card" css={styles.container}>
      <Grid container alignItems="center" css={styles.gridContainer}>
        <Grid item xs={12} sm={8}>
          <Typography2 variant="subtitleMedium" component="h3" css={styles.title}>
            {courseName}
          </Typography2>
          <div css={styles.supportText}>
            <Typography2 variant="bodySecondary" component="span" color="supportText">
              {_t('Assesses all skill capabilities')}
            </Typography2>
            {formattedTimeCommitment && (
              <React.Fragment>
                <Typography2 variant="bodySecondary" component="span" color="supportText">
                  •
                </Typography2>
                <Typography2 variant="bodySecondary" component="span" color="supportText">
                  {formattedTimeCommitment}
                </Typography2>
              </React.Fragment>
            )}
          </div>
        </Grid>
        <Grid item xs={12} sm={4}>
          <div css={styles.buttonContainer}>
            <SkillAssessmentCourseActionButton
              isEnrolledInPathway={isEnrolledInPathway}
              isEnrolledInCourse={isEnrolledInCourse}
              isProgramMember={isProgramMember}
              courseId={courseId}
              courseSlug={courseSlug}
              programId={programId}
              redirectToAssessment={redirectToAssessment}
              assessmentId={assessmentId}
              pathwayCollectionId={pathwayCollectionId}
              pathwayId={pathwayId}
              programSlug={programSlug}
              thirdPartyOrganizationSlug={thirdPartyOrganizationSlug}
              disabled={disabled}
              activateProgramAccessModal={activateProgramAccessModal}
              isEnrollingCallback={isEnrollingCallback}
              trackingData={trackingData}
              trackingButtonName="verified_skills_progress_dialog_start_assessment"
              size="small"
              variant="secondary"
              buttonIcon={buttonIcon}
              buttonIconPosition={buttonIconPosition}
            >
              {buttonText || _t('Start assessment')}
            </SkillAssessmentCourseActionButton>
          </div>
        </Grid>
        <Grid item xs={12}>
          <Divider css={styles.divider} />
        </Grid>
      </Grid>
    </div>
  );
};

export default SkillProgressTakeAssessmentControls;
